import { useEffect, useRef, useState } from 'react';

const phases = [
  {
    phase: '01',
    label: '準備段階',
    title: '不安を、戦略に変える。',
    desc: '立ち上げ前の混沌とした状況を整理し、明確なロードマップを構築。最初の一歩を、確信を持って踏み出せるよう全力でサポートします。',
  },
  {
    phase: '02',
    label: '立ち上げ期',
    title: '重圧を、推進力に変える。',
    desc: 'ローンチの重圧を一人で背負わせません。マーケティング・オペレーション・ブランディングを同時並行で動かし、最速で市場に定着させます。',
  },
  {
    phase: '03',
    label: '成長期',
    title: '壁を、飛躍台に変える。',
    desc: '成長の踊り場に差し掛かったとき、次の突破口を見つけるのが私たちの役割。データと現場感覚を融合させた戦略で、さらなる高みへ。',
  },
];

export default function CommitmentSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activePhase, setActivePhase] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePhase((prev) => (prev + 1) % phases.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="commitment" className="relative bg-[#f5f3ef] py-24 md:py-36 overflow-hidden" ref={sectionRef}>
      {/* Decorative large text */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none select-none overflow-hidden">
        <span
          className="text-[#1a1a1a]/[0.03] font-black"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(8rem, 20vw, 20rem)',
            lineHeight: 1,
          }}
        >
          NEVER
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left: Image */}
          <div
            className={`relative transition-all duration-1000 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
              <img
                src="https://static.readdy.ai/image/d40b343a735e9ff1d1cb56d3b54df611/c7da9aec5b9d2b3afa83ee7508b2943f.png"
                alt="Commitment"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#f5f3ef]/50 via-transparent to-transparent" />

              {/* Floating badge */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/80 backdrop-blur-md border border-[#1a1a1a]/8 rounded-2xl p-5">
                <p className="text-[#b4a078] text-[10px] tracking-[0.3em] uppercase mb-2">Our Promise</p>
                <p className="text-[#1a1a1a] text-sm font-light leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  「次の時代の売上づくり」という不安要素は、ここで終わります。
                </p>
              </div>
            </div>

            {/* Decorative corner */}
            <div className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-[#b4a078]/40 rounded-tl-2xl" />
            <div className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-[#b4a078]/40 rounded-br-2xl" />
          </div>

          {/* Right: Content */}
          <div
            className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-8 h-px bg-[#b4a078]" />
              <span className="text-[#b4a078] text-[10px] tracking-[0.4em] uppercase">Commitment</span>
            </div>

            <h2
              className="text-[#1a1a1a] font-light leading-tight mb-8"
              style={{ fontFamily: "'Noto Serif JP', serif", fontSize: 'clamp(1.6rem, 3.5vw, 3rem)' }}
            >
              立ち上げから成長まで、<br />
              <span className="text-[#1a1a1a]/40">ともに並走する。</span>
            </h2>

            <p className="text-[#1a1a1a]/50 text-sm leading-loose mb-12">
              多くのコンサルが「部分」で終わる中、私たちは「一緒に並走」を誓います。
              準備段階の不安、立ち上げの重圧、そして成長期の壁。
              すべてのプロセスで、トップクラスの知能があなたのチームに加わります。
            </p>

            {/* Phase tabs */}
            <div className="space-y-0">
              {phases.map((phase, i) => (
                <button
                  key={phase.phase}
                  onClick={() => setActivePhase(i)}
                  className={`w-full text-left border-b transition-all duration-500 cursor-pointer ${
                    i === activePhase ? 'border-[#b4a078]/50' : 'border-[#1a1a1a]/8'
                  }`}
                >
                  <div className="py-5 flex items-start gap-5">
                    <span
                      className={`text-xs tracking-widest font-light transition-colors duration-300 mt-0.5 ${
                        i === activePhase ? 'text-[#b4a078]' : 'text-[#1a1a1a]/25'
                      }`}
                    >
                      {phase.phase}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={`text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${
                            i === activePhase ? 'text-[#b4a078]' : 'text-[#1a1a1a]/25'
                          }`}
                        >
                          {phase.label}
                        </span>
                        <i
                          className={`ri-arrow-right-line text-xs transition-all duration-300 ${
                            i === activePhase ? 'text-[#b4a078] translate-x-1' : 'text-[#1a1a1a]/15'
                          }`}
                        />
                      </div>
                      <p
                        className={`text-sm font-light mb-2 transition-colors duration-300 ${
                          i === activePhase ? 'text-[#1a1a1a]' : 'text-[#1a1a1a]/35'
                        }`}
                        style={{ fontFamily: "'Noto Serif JP', serif" }}
                      >
                        {phase.title}
                      </p>
                      <div
                        className={`overflow-hidden transition-all duration-500 ${
                          i === activePhase ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <p className="text-[#1a1a1a]/45 text-xs leading-relaxed">{phase.desc}</p>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
